"use client"

import { t } from "@/languages/i18n"
import { DefaultImage } from "@/lib/external-components"
import { getLocalizedHref } from "@/lib/locale-utils"
import { generateImagePath } from "@/lib/utils"
import { PostData } from "@/types/types"
import Image from "next/image"
import Link from "next/link"

interface PostCardProps {
  post: PostData
  locale?: string
  className?: string
  showReadMore?: boolean
}

export const PostCard: React.FC<PostCardProps> = ({ post, className = "", showReadMore = true }) => {
  // Slug thuộc locale nào thì URL theo locale đó → lấy thẳng từ chính bài.
  const blogUrl = getLocalizedHref(post.attributes.locale, `/${post.attributes.slug}`)
  const shortDescription = post.attributes.excerpt
    ? post.attributes.excerpt
    : post.attributes.content.slice(0, 200) + "..."
  return (
    <Link
      href={blogUrl}
      className={`flex flex-col gap-4 lg:max-w-[373.33px] max-w-[343px] lg:mb-6 group cursor-pointer ${className}`}
    >
      <div className="relative h-[270px] lg:w-[373.33px] w-[343px] rounded-2xl overflow-hidden">
        <Image
          src={
            post.attributes?.image?.data?.attributes?.url
              ? generateImagePath(
                  post.attributes.image.data.attributes.url,
                  "medium",
                  post.attributes.image.data.attributes.formats,
                )
              : DefaultImage
          }
          alt={post.attributes.name || "Article image"}
          fill
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 375px"
          className="object-cover rounded-2xl transition-transform duration-500 ease-in-out group-hover:scale-110"
          loading="lazy"
          quality={75}
        />
      </div>

      <div className="flex flex-row gap-2 items-center">
        <div className="flex flex-wrap gap-2">
          {post.attributes?.tags?.data &&
            post.attributes.tags.data.map((tag, index) => (
              <p
                key={index}
                className="py-1 px-2 rounded-md bg-[#EBEDF0] text-center text-[#4F4F4F] text-sm flex flex-row gap-1 items-center"
              >
                <Image src={""} alt="Blog tag icon" width={16} height={16} />
                {tag.attributes.name}
              </p>
            ))}
        </div>

        {post.attributes.createdAt && (
          <p className="text-[#999999] font-normal text-sm text-nowrap" style={{ lineHeight: "19px" }}>
            •{" "}
            {new Intl.DateTimeFormat("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            }).format(new Date(post.attributes.createdAt))}
          </p>
        )}
      </div>

      {post.attributes.name && (
        <p
          className="font-semibold text-2xl text-[#242F3E] group-hover:text-[#0B9444] transition-colors duration-300"
          style={{ lineHeight: "28px" }}
        >
          {post.attributes.name}
        </p>
      )}

      {shortDescription && (
        <div
          className="text-[#4F4F4F] text-base font-normal line-clamp-3"
          style={{ lineHeight: "22px" }}
          dangerouslySetInnerHTML={{ __html: shortDescription }}
        />
      )}

      {showReadMore && (
        <div className="flex flex-row w-full items-center justify-between pb-4 border-b border-[#EBEDF0] group-hover:border-[#0B9444] transition-colors duration-300">
          {post.attributes.createdAt && (
            <p className="text-[#999999] font-normal text-sm" style={{ lineHeight: "19px" }}>
              {new Intl.DateTimeFormat("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              }).format(new Date(post.attributes.createdAt))}
            </p>
          )}

          <p
            className="text-[#0B9444] font-normal text-sm group-hover:font-semibold group-hover:translate-x-1 transition-all duration-300"
            style={{ lineHeight: "19px" }}
          >
            {t("Read more")}
          </p>
        </div>
      )}
    </Link>
  )
}

export default PostCard
